import { czyWidziDostawy } from '@/lib/auth/dostepZrodel';
import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { DailyTrendChart } from '@/components/DailyTrendChart';
import { Karta } from '@/components/Karta';
import { PasSekcji } from '@/components/PasSekcji';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { MetricBasisChips } from '@/components/MetricBasisChips';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import { DataQualityBadge } from '@/components/states';
import { PrzyciskDowodu, type Dowod, type KlasyfikacjaZrodla } from '@/components/SzufladaDowodu';
import { WymagaUwagi, type PozycjaUwagi } from '@/components/WymagaUwagi';
import { buildInfo, describeBuild, shortSha } from '@/config/buildInfo';
import { config, describeDataOrigin, type ApiSystem } from '@/config/env';
import type { DailyTrendPoint } from '@/lib/api/analytics';
import type { DeliveryTrendPoint } from '@/lib/api/drive';
import { describeApiError } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/AuthContext';
import { assessSample, type DataQualityAssessment } from '@/lib/dataQuality';
import { formatCount, formatGrosze, formatRange } from '@/lib/format';
import type { MetricBasis } from '@/lib/metricBasis';
import { opiszDatyKarty, dekodujDateKarty } from '@/lib/dataKarty';
import { OPIS_SWIEZOSCI, ocenSwiezoscReferencyjna } from '@/lib/swiezoscReferencyjna';
import { Miernik, ZnacznikZrodla, poziomZJakosci, stanZKlasyfikacji } from '@/components/wykresy/Miernik';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import { useServices } from '@/lib/ServicesContext';
import {
  useDeliveryBySchool,
  useDeliverySummary,
  useDeliveryTrend,
  useDishCatalog,
  useDishSales,
  useRestaurantSales,
  useSalesDeliveryCoincidence,
  useSchoolsDictionary,
  useRestaurantsDictionary,
  useSchoolSales,
} from '@/lib/useAnalytics';
import { useDateRange } from '@/lib/useDateRange';
import { klasyfikacjaDrive, klasyfikacjaKatalogu, swiezoscKatalogu, swiezoscDrive } from '@/lib/pochodzenieDanych';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * JAKOŚĆ DANYCH — przyrząd pomiarowy, nie lista ostrzeżeń
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * PYTANIE EKRANU: czy na tym, co widać na pozostałych ekranach, wolno oprzeć decyzję — a jeżeli
 * nie, to które źródło jest słabym ogniwem i dlaczego.
 *
 * DLACZEGO TO NIE JEST PRZYPIS POD WYKRESEM
 * Pytanie „czy mogę na tym oprzeć decyzję" pada w tej firmie równie często jak „ile sprzedaliśmy".
 * Odpowiedź schowana w stopce znaczy tyle, co jej brak: przeczyta ją ten, kto już jest ostrożny,
 * a nie ten, kto właśnie ma zadzwonić do restauracji. Dlatego jakość danych jest osobną
 * przestrzenią z własną liczbą-bohaterem, a nie gwiazdką przy cudzej liczbie.
 *
 * ┌──────────────────────────────────────────────────────────────────────────────────────────┐
 * │ TRZY RÓŻNE ZDANIA, KTÓRE WYGLĄDAJĄ TAK SAMO W ODPOWIEDZI API:                             │
 * │   • ŚWIEŻOŚĆ NIEUSTALONA — źródło odpowiada, ale nie mówi, z którego momentu pochodzi.   │
 * │   • PUSTY ZBIÓR          — zero wierszy do policzenia; suma z pustego zbioru to też 0.   │
 * │   • BRAK SPRZEDAŻY       — komplet wierszy, wynik zerowy. To ZMIERZONE zero.             │
 * │ Pomylenie pierwszych dwóch z trzecim prowadzi wprost do wniosku „nic się nie sprzedaje" — │
 * │ czyli do zdania o firmie wyciągniętego ze stanu integracji.                              │
 * └──────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * ŻADNYCH DANYCH DEMONSTRACYJNYCH. Wszystkie liczby, daty i etykiety na tym ekranie pochodzą
 * z odpowiedzi realnych API albo z metadanych artefaktu. Tam, gdzie odczytu nie było, ekran mówi
 * „nie było odczytu" — nie zgaduje daty i nie wpisuje zera.
 */

// ── Stan ODCZYTU (nie: stan biznesu) ──────────────────────────────────────────
// Świadomie sześć wartości, a nie „jest / nie ma". Każda z nich prowadzi do innego działania:
// pustym zbiorem zajmuje się integracja, brakiem sesji — operator, brakiem pola — kontrakt API.

type StanOdczytu = 'CZEKA' | 'NIEPODLACZONE' | 'BLAD' | 'BRAK_POLA' | 'PUSTY_ZBIOR' | 'MA_DANE';

const OPIS_STANU: Record<StanOdczytu, string> = {
  CZEKA: 'odczyt w toku',
  NIEPODLACZONE: 'brak sesji — nie było odczytu',
  BLAD: 'błąd odczytu',
  BRAK_POLA: 'pole nieobecne w odpowiedzi',
  PUSTY_ZBIOR: 'pusty zbiór',
  MA_DANE: 'ma dane',
};

const BARWA_STANU: Record<StanOdczytu, string> = {
  CZEKA: 'var(--neutralny)',
  NIEPODLACZONE: 'var(--neutralny)',
  BLAD: 'var(--krytyczny)',
  BRAK_POLA: 'var(--krytyczny)',
  PUSTY_ZBIOR: 'var(--uwaga)',
  MA_DANE: 'var(--sukces)',
};

/**
 * Grupa źródła. Rozdzielenie SPRZEDAŻY od DANYCH REFERENCYJNYCH jest tu treścią, nie ozdobą:
 * słownik szkół i wymiar szkół w sprzedaży nazywają się tak samo, a mają osobne źródła, osobną
 * świeżość i osobne blokery. Wspólna lista zachęcała do przeniesienia świeżości jednego na drugie.
 */
type GrupaZrodla = 'SPRZEDAZ' | 'REFERENCJA' | 'DOSTAWY' | 'MIEDZYSYSTEMOWE' | 'WYDANIE';

/** Kolejność sekcji: sprzedaż pierwsza, bo to jej brak jest blokerem programu. */
const GRUPY_KOLEJNOSC: readonly GrupaZrodla[] = ['SPRZEDAZ', 'REFERENCJA', 'DOSTAWY', 'MIEDZYSYSTEMOWE', 'WYDANIE'];

const OPIS_GRUPY: Record<GrupaZrodla, { tytul: string; czym: string }> = {
  SPRZEDAZ: {
    tytul: 'Sprzedaż',
    czym: 'co i za ile faktycznie kupiono. Świeżość źródła transakcji jest NIEUSTALONA i żadne inne źródło tego nie zmienia.',
  },
  REFERENCJA: {
    tytul: 'Dane referencyjne',
    czym: 'co w ogóle istnieje: szkoły, restauracje, karta dań. To jest OFERTA i SŁOWNIKI — nie sprzedaż.',
  },
  DOSTAWY: { tytul: 'Dostawy', czym: 'przebieg dowozów z aplikacji kierowców.' },
  MIEDZYSYSTEMOWE: { tytul: 'Międzysystemowe', czym: 'jakość złączenia danych z różnych systemów.' },
  WYDANIE: { tytul: 'Wydanie', czym: 'rodowód artefaktu, który właśnie oglądasz.' },
};

interface WierszZrodla {
  klucz: string;
  /**
   * Stabilny adres wiersza dla walidacji DOM (atrybut data-cc-card-id na <TableRow>).
   * Pole WYMAGANE świadomie: dopasowanie po tekście nie rozstrzyga — „Szkoły" występuje
   * w tej tabeli dwa razy (wymiar sprzedaży i słownik), a nazwy kolumn powtarzają się
   * między ekranami. Nowy wiersz bez identyfikatora ma nie przejść kompilacji.
   */
  kartaId: string;
  /**
   * Adres SZUFLADY DOWODU tego wiersza — osobny od `kartaId`, bo w inwentarzu to osobna karta.
   * Wypełniony tylko tam, gdzie dowód niesie własną treść ujętą w inwentarzu (raport złączenia
   * kluczy szkół z dostawami). Przycisk dowodu ma wszędzie tę samą etykietę „Dowód", więc bez
   * identyfikatora z miejsca użycia nie da się go wskazać jednoznacznie.
   */
  dowodId?: string;
  grupa: GrupaZrodla;
  nazwa: string;
  /** Czym to źródło JEST — jednym zdaniem, bez marketingu. */
  czym: string;
  srodowisko: string;
  klasyfikacja: KlasyfikacjaZrodla;
  swiezosc: string;
  /** Czy dało się ustalić moment pochodzenia danych. To zasila liczbę-bohatera. */
  swiezoscUstalona: boolean;
  /** Najpóźniejsza data widoczna w danych. null = nie ma czego pokazać, NIGDY zero. */
  ostatniaData: string | null;
  /** Co ta data znaczy — bez tego „2026-08-14" jest ozdobą. */
  oDacie: string;
  jakosc: DataQualityAssessment;
  bloker: string | null;
  stan: StanOdczytu;
  podstawa?: MetricBasis;
  dowod: Dowod;
}

// ── Wyprowadzenia dat (czyste funkcje — dają się sprawdzić bez renderu) ───────

/** Znacznik czasu źródła → data w czasie warszawskim. Nieparsowalny znacznik to brak daty. */
function dataZnacznika(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-CA', { timeZone: 'Europe/Warsaw' });
}

/** Najpóźniejsza ze znanych dat ISO. Porównanie leksykalne wystarcza dla YYYY-MM-DD. */
function najpozniejsza(daty: readonly (string | null)[]): string | null {
  const znane = daty.filter((d): d is string => d !== null).sort();
  return znane.at(-1) ?? null;
}

/**
 * Ostatni dzień okna, w którym sprzedaż była różna od zera.
 * Dzień z zerem ZOSTAJE w serii (to prawdziwy dzień bez sprzedaży), ale nie jest „ostatnią znaną
 * datą" — inaczej pusty zbiór wyglądałby jak dane sięgające do końca okna.
 */
function ostatniDzienZeSprzedaza(punkty: readonly DailyTrendPoint[] | undefined): string | null {
  if (!punkty) return null;
  return punkty.filter((p) => p.orders > 0 || p.sales_grosze > 0).at(-1)?.date ?? null;
}

/** Ostatni dzień okna z co najmniej jedną dostawą. */
function ostatniDzienZDostawa(seria: readonly DeliveryTrendPoint[] | undefined): string | null {
  if (!seria) return null;
  return seria.filter((p) => p.deliveries > 0).at(-1)?.date ?? null;
}

/**
 * Dlaczego daty nie ma. „Brak danych" bez powodu zmusza do zgadywania, czy źródło milczy,
 * czy jeszcze nie odpowiedziało — a to są dwie zupełnie różne sprawy dla dwóch różnych osób.
 */
function powodBrakuDaty(stan: StanOdczytu): string | null {
  switch (stan) {
    case 'CZEKA':
      return 'odczyt w toku';
    case 'NIEPODLACZONE':
      return 'nie było odczytu — brak sesji';
    case 'BLAD':
    case 'BRAK_POLA':
      return 'odczyt nie powiódł się';
    case 'PUSTY_ZBIOR':
      return 'pusty zbiór — nie ma z czego odczytać daty';
    case 'MA_DANE':
      return null;
  }
}

// ── Sondy zdrowia (logika przeniesiona z ekranu „Status połączeń") ────────────
// Sonda odpowiada na pytanie POŚREDNIE, ale ważne dla jakości danych: gdy backend nie odpowiada,
// pustka na ekranie analitycznym nie jest wynikiem, tylko brakiem odczytu. Sondy są READ-ONLY —
// sonda, która cokolwiek zapisuje, nie jest sondą.

interface Sonda {
  system: ApiSystem;
  etykieta: string;
  sciezka: string;
}

// JEDNA SONDA, bo jest jedno wejście. Trzy osobne sondy do CRM, Drive i systemu głównego
// opisywały świat, w którym przeglądarka rozmawiała z trzema backendami — a rozmawia z jednym.
//
// Adres MUSI być pełny (`/api/cc/health`), a nie samo `/health`. Poprzednia wersja pytała
// o `/health` na korzeniu i dostawała trzy razy 404, które trafiały do konsoli jako
// „Failed to load resource" bez adresu — czyli residuum nie do zdiagnozowania. Znalazł to
// dopiero przebieg walidacyjny, po dołożeniu adresu do raportu.
const SONDY: readonly Sonda[] = [
  { system: 'crm', etykieta: 'Backend Control Center', sciezka: '/api/cc/health' },
];

function useSonda(sonda: Sonda) {
  const { api } = useServices();
  return useQuery({
    queryKey: ['probe', sonda.system],
    queryFn: async () => {
      const start = performance.now();
      await api.get<unknown>(sonda.system, sonda.sciezka);
      return Math.round(performance.now() - start);
    },
    retry: false,
    staleTime: 15_000,
  });
}

function WierszSondy({ sonda }: { sonda: Sonda }) {
  const { api } = useServices();
  const { data, error, isLoading } = useSonda(sonda);
  return (
    <TableRow>
      <TableCell>{sonda.etykieta}</TableCell>
      <TableCell sx={{ fontFamily: 'var(--krój-mono)', fontSize: 12, color: 'var(--tekst-2)' }}>
        {`${api.baseFor(sonda.system)}${sonda.sciezka}`}
      </TableCell>
      <TableCell>
        {isLoading ? (
          <Chip size="small" label="sprawdzam…" />
        ) : error ? (
          <Chip size="small" color="error" variant="outlined" label="niedostępne" />
        ) : (
          <Chip size="small" color="success" variant="outlined" label={`OK · ${data} ms`} className="sygnal-liczba" />
        )}
      </TableCell>
      <TableCell>
        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
          {error ? describeApiError(error) : '—'}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

/** Definicja w bloku rozróżnienia semantycznego. Kolor jest etykietą rodzaju sygnału, nie ozdobą. */
function Rozroznienie({ barwa, tytul, rodzaj, tresc }: { barwa: string; tytul: string; rodzaj: string; tresc: string }) {
  return (
    <Box sx={{ display: 'flex', gap: 'var(--o2)' }}>
      <Box sx={{ width: 4, borderRadius: '2px', background: barwa, flex: 'none' }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 13.5 }}>{tytul}</Typography>
        <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)', display: 'block', lineHeight: 1.6 }}>
          {rodzaj}
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--tekst-2)' }}>
          {tresc}
        </Typography>
      </Box>
    </Box>
  );
}

function WierszRodowodu({ etykieta, wartosc, rozjazd, testid }: { etykieta: string; wartosc: string; rozjazd?: string | null; testid?: string }) {
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', py: 0.5, borderBottom: '1px dashed var(--linia)' }}>
      <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)', minWidth: 200, flex: 'none' }}>
        {etykieta}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          className="sygnal-liczba"
          sx={{ fontFamily: 'var(--krój-mono)', fontSize: 12, wordBreak: 'break-all' }}
          data-testid={testid}
        >
          {wartosc}
        </Typography>
        {rozjazd ? (
          <Typography variant="caption" sx={{ color: 'var(--krytyczny)', display: 'block' }}>
            {rozjazd}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

/**
 * Wiersz „etykieta → liczba → co ta liczba znaczy".
 *
 * DOPISEK JEST OBOWIĄZKOWY, nie ozdobny: każda liczba na tym ekranie da się przeczytać jako
 * ocena („4 braki — źle"), a prawie żadna nią nie jest. Zdanie pod liczbą mówi, czego ona
 * dotyczy i czego z niej NIE wynika — i dlatego stoi przy niej, nie w przypisie na dole.
 */
function Wiersz({ etykieta, wartosc, dopisek }: { etykieta: string; wartosc: string; dopisek: string }) {
  return (
    <Box sx={{ py: 1, borderBottom: '1px dashed var(--linia)' }}>
      <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)' }}>
          {etykieta}
        </Typography>
        <Typography variant="body2" className="sygnal-liczba" sx={{ fontWeight: 700, textAlign: 'right' }}>
          {wartosc}
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 0.25, maxWidth: '78ch' }}>
        {dopisek}
      </Typography>
    </Box>
  );
}

export function JakoscDanychPage() {
  const auth = useAuth();
  const { api } = useServices();
  const [range] = useDateRange();
  const okno = formatRange(range.date_from, range.date_to);

  // Te same haki, z których korzystają ekrany analityczne. Stan dostępności MUSI pochodzić
  // z realnego odczytu — tabela wpisana na sztywno pokazywałaby zdrowie, którego nikt nie zmierzył.
  const schools = useSchoolSales();
  const dishes = useDishSales();
  const slownikSzkol = useSchoolsDictionary();
  const slownikRestauracji = useRestaurantsDictionary();
  const restaurants = useRestaurantSales();
  const catalog = useDishCatalog();
  const delivery = useDeliverySummary();
  const bySchool = useDeliveryBySchool();
  const trend = useDeliveryTrend();
  const cross = useSalesDeliveryCoincidence();

  const driveConnected = czyWidziDostawy(auth);
  const raportZlaczenia = cross.data?.available === true ? cross.data.join : undefined;
  /*
   * UWAGA NA CUDZE ZDANIE, KTÓREGO NIE WOLNO POWTÓRZYĆ.
   * `assessJoin` (lib/crossSystem.ts) przy zerowej liczbie szkół po stronie SPRZEDAŻY zwraca
   * `matched === 0` i uzasadnienie „zbiory identyfikatorów nie mają części wspólnej — sprzedaż
   * i dostawy dotyczą rozłącznych szkół". To jest zdanie o DANYCH, a prawda jest wtedy techniczna:
   * jedna strona nie zwróciła niczego, więc nie ma czego dopasowywać. Ten ekran istnieje po to,
   * żeby takich zdań nie przepuszczać dalej — nadpisujemy uzasadnienie tam, gdzie jest nieprawdziwe.
   */
  const brakStronySprzedazy = raportZlaczenia !== undefined && raportZlaczenia.sales_schools === 0;
  const brakStronyDostaw =
    raportZlaczenia !== undefined && raportZlaczenia.delivery_schools === 0 && (bySchool.data?.schools.length ?? 0) === 0;
  let powodZlaczenia: string | null = raportZlaczenia?.reason ?? null;
  if (brakStronySprzedazy && brakStronyDostaw) {
    powodZlaczenia = 'Obie strony złączenia są puste — nie ma czego dopasowywać. To nie jest rozłączność zbiorów.';
  } else if (brakStronySprzedazy) {
    powodZlaczenia =
      `Strona sprzedaży nie zwróciła ani jednej szkoły, więc nie ma czego dopasowywać (dostawy: ` +
      `${formatCount(raportZlaczenia?.delivery_schools ?? null)} szkół). To NIE znaczy, że sprzedaż i dostawy ` +
      'dotyczą rozłącznych szkół.';
  } else if (brakStronyDostaw) {
    powodZlaczenia =
      `Strona dostaw nie zwróciła ani jednej szkoły, więc nie ma czego dopasowywać (sprzedaż: ` +
      `${formatCount(raportZlaczenia?.sales_schools ?? null)} szkół).`;
  }
  const oknoPorownawcze = dishes.data?.range.comparison
    ? formatRange(dishes.data.range.comparison.date_from, dishes.data.range.comparison.date_to)
    : null;

  // ── 1. SPRZEDAŻ CRM ─────────────────────────────────────────────────────────
  const ziarnoSprzedazy = {
    wierszy: schools.data?.summary.total_orders ?? null,
    bytow: schools.data?.schools.length ?? null,
  };
  const sprzedazPusta = czyPustyZbior(ziarnoSprzedazy);
  // Wartość pokazujemy WYŁĄCZNIE przez wartoscDoPokazania: suma z pustego zbioru też wynosi 0,
  // a „0,00 zł" w miejscu, gdzie nie było czego sumować, jest zmyśleniem wyniku.
  const wartoscSprzedazy = wartoscDoPokazania(schools.data?.summary.total_sales_grosze, ziarnoSprzedazy);

  const seriaSprzedazy = schools.data?.charts?.daily_trend;
  const dniZeSprzedaza = seriaSprzedazy ? seriaSprzedazy.filter((p) => p.orders > 0 || p.sales_grosze > 0).length : null;
  const ostatniDzienSprzedazy = ostatniDzienZeSprzedaza(seriaSprzedazy);

  // Sprzedaż czytają DWA zapytania (szkoły i dania). Jeżeli któreś padło, odczyt źródła nie jest
  // pełny — i tak to nazywamy, zamiast pokazywać połowę obrazu jako całość.
  let stanSprzedazy: StanOdczytu = 'CZEKA';
  if (schools.error || dishes.error) stanSprzedazy = 'BLAD';
  else if (schools.data) stanSprzedazy = sprzedazPusta ? 'PUSTY_ZBIOR' : 'MA_DANE';

  let jakoscSprzedazy: DataQualityAssessment = { quality: 'UNKNOWN', reason: 'Odczyt w toku.' };
  if (schools.error) jakoscSprzedazy = { quality: 'UNKNOWN', reason: describeApiError(schools.error) };
  else if (dishes.error)
    jakoscSprzedazy = { quality: 'UNKNOWN', reason: `Rozbicie na dania nie odpowiedziało: ${describeApiError(dishes.error)}` };
  else if (schools.data && dniZeSprzedaza === null)
    jakoscSprzedazy = { quality: 'UNKNOWN', reason: 'Odpowiedź nie zawiera serii dziennej — pokrycia okna nie da się policzyć.' };
  else if (dniZeSprzedaza !== null)
    jakoscSprzedazy = assessSample({ sampleSize: dniZeSprzedaza, expectedSize: dishes.data?.range.days ?? null });

  const dowodSprzedazy: Dowod = {
    tytul: 'Sprzedaż CRM — świeżość i ostatni dzień ze sprzedażą',
    etykietaWartosci: 'Najpóźniejszy dzień ZE SPRZEDAŻĄ w oknie',
    wartosc: ostatniDzienSprzedazy,
    podstawa: { value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' },
    zrodlo: `CRM ${api.baseFor('crm')} → /api/crm/analytics/school-sales (charts.daily_trend)`,
    // Klasyfikacja jest tu twarda i celowa: to jest największa otwarta luka programu.
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — odpowiedź nie niesie ani momentu odczytu, ani znacznika synchronizacji',
    oknoBiezace: okno,
    oknoPorownawcze,
    wyliczenie:
      'Ostatni punkt serii dziennej (kubełkowanej dniem dostawy), w którym liczba zamówień albo wartość katalogowa jest różna od zera.',
    szczegoly: [
      ['Stan odczytu', OPIS_STANU[stanSprzedazy]],
      ['Zamówienia w oknie', formatCount(ziarnoSprzedazy.wierszy)],
      ['Szkoły w wyniku', formatCount(ziarnoSprzedazy.bytow)],
      ['Wartość wg cennika', wartoscSprzedazy === null ? 'brak danych — nie ma czego sumować' : formatGrosze(wartoscSprzedazy)],
      [
        'Dni ze sprzedażą',
        dniZeSprzedaza === null
          ? 'brak serii dziennej w odpowiedzi'
          : `${formatCount(dniZeSprzedaza)} z ${formatCount(dishes.data?.range.days ?? null)}`,
      ],
      ['Dania w rozbiciu sprzedaży', formatCount(dishes.data?.summary.dishes_count ?? null)],
      ['Wersja silnika dań', dishes.data?.engine_version ?? 'nieodczytana'],
    ],
    proba: `${formatCount(ziarnoSprzedazy.wierszy)} zamówień`,
    jakoscDanych: jakoscSprzedazy.reason,
    ograniczenia: [
      ...(sprzedazPusta
        ? [
            'ŹRÓDŁO ZWRÓCIŁO PUSTY ZBIÓR: zero zamówień i zero szkół w tym oknie. To NIE jest zmierzone zero — ' +
              'nie ma czego sumować, więc nie ma też z czego odczytać daty.',
          ]
        : []),
      'Data pochodzi wyłącznie z WYBRANEGO OKNA. Sprzedaż sprzed okna nie jest tu widoczna, więc „ostatni dzień" ' +
        'nie znaczy „ostatni w historii".',
      ...(dishes.data?.caveats ?? []),
    ],
    czegoNieWynika:
      'Z tej daty NIE wynika, kiedy CRM ostatnio się zsynchronizował. Data jest wzięta z TREŚCI danych (dnia dostawy), ' +
      'a nie ze znacznika odczytu — gdyby synchronizacja stała od tygodni, ostatni dzień ze sprzedażą i tak wyglądałby ' +
      'wiarygodnie. Nie wynika z niej również, że po tej dacie nic się nie sprzedało: równie dobrze może znaczyć, ' +
      'że po tej dacie nic do CRM nie dotarło.',
  };

  // ── 2. DOSTAWY DRIVE ────────────────────────────────────────────────────────
  const ziarnoDostaw = {
    wierszy: delivery.data?.current.deliveries ?? null,
    bytow: bySchool.data?.schools.length ?? null,
  };
  const dostawyPuste = czyPustyZbior(ziarnoDostaw);
  const ostatniDzienDostawy = ostatniDzienZDostawa(trend.data?.series);

  let stanDostaw: StanOdczytu = 'CZEKA';
  if (!driveConnected) stanDostaw = 'NIEPODLACZONE';
  else if (delivery.error ?? bySchool.error ?? trend.error) stanDostaw = 'BLAD';
  else if (delivery.data) stanDostaw = dostawyPuste ? 'PUSTY_ZBIOR' : 'MA_DANE';

  let jakoscDostaw: DataQualityAssessment = { quality: 'UNKNOWN', reason: 'Odczyt w toku.' };
  if (!driveConnected) jakoscDostaw = { quality: 'UNKNOWN', reason: 'Bez sesji systemu kierowców nie było odczytu.' };
  else if (delivery.error) jakoscDostaw = { quality: 'UNKNOWN', reason: describeApiError(delivery.error) };
  else if (delivery.data)
    jakoscDostaw = { quality: delivery.data.current.data_quality, reason: delivery.data.current.data_quality_reason };

  const dowodDostaw: Dowod = {
    tytul: 'Dostawy Drive — świeżość i ostatni dzień z dostawą',
    etykietaWartosci: 'Najpóźniejszy dzień Z DOSTAWĄ w oknie',
    wartosc: ostatniDzienDostawy,
    podstawa: { time: 'DELIVERY_DATE' },
    zrodlo: `Drive ${api.baseFor('drive')} → /api/admin/analytics/delivery-trend oraz delivery-summary`,
    klasyfikacja: klasyfikacjaDrive(),
    swiezosc: driveConnected
      ? 'odczyt bieżący ze środowiska TEST (zbiór kontrolowany)'
      : 'nieustalona — bez sesji nie było żadnego odczytu',
    oknoBiezace: okno,
    wyliczenie: 'Ostatni punkt dziennej serii dostaw, w którym liczba dostaw jest różna od zera.',
    szczegoly: [
      ['Stan odczytu', OPIS_STANU[stanDostaw]],
      ['Dostawy w oknie', formatCount(ziarnoDostaw.wierszy)],
      ['Szkoły z dostawami', formatCount(ziarnoDostaw.bytow)],
      ['Bez utrwalonego statusu', formatCount(delivery.data?.current.without_snapshot ?? null)],
      ['Bez zapisanego odchylenia', formatCount(delivery.data?.current.without_delta ?? null)],
      ['Punkty serii dziennej', formatCount(trend.data?.series.length ?? null)],
    ],
    proba: `${formatCount(ziarnoDostaw.wierszy)} dostaw`,
    jakoscDanych: jakoscDostaw.reason,
    ograniczenia: [
      'Zbiór KONTROLOWANY: kod API i schemat bazy są realne, dane wprowadzono na potrzeby weryfikacji. ' +
        'Wolumen i rozkład nie odpowiadają produkcji.',
      ...(dostawyPuste ? ['W tym oknie nie ma ani jednej dostawy i ani jednej szkoły z dostawą — nie ma czego mierzyć.'] : []),
      ...(delivery.data?.caveats ?? []),
    ],
    czegoNieWynika:
      'Z tego, że dostawy są świeże, NIE wynika, że są kompletne: dostawa bez utrwalonego statusu nie wchodzi do ' +
      'punktualności i nie widać jej w tej dacie. Nie wynika z niej też nic o świeżości sprzedaży — to osobny system ' +
      'z osobną bazą i osobnym zasilaniem.',
  };

  // ── 3. KATALOG DAŃ ──────────────────────────────────────────────────────────
  // Jedyne źródło, które podaje swój własny moment odczytu (last_seen_at przy każdym wpisie).
  const daniaKatalogu = catalog.data?.dishes;
  const restauracjeWKatalogu = daniaKatalogu
    ? new Set(daniaKatalogu.map((d) => d.restaurant_id).filter((id): id is number => id !== null)).size
    : null;
  const zrodlaWpisow = daniaKatalogu
    ? [...new Set(daniaKatalogu.map((d) => d.source).filter((s): s is string => Boolean(s)))]
    : [];
  const ostatniOdczytKatalogu = najpozniejsza((daniaKatalogu ?? []).map((d) => dataZnacznika(d.last_seen_at)));

  // Proweniencja ODCZYTU zapisana przy synchronizacji. Klasyfikacja katalogu WYNIKA z niej,
  // zamiast być wpisana w ekran: bez daty karty i bez znanego źródła nie wolno powiedzieć
  // „realne dane referencyjne", bo katalog nieznanego wieku niczym nie różni się od kopii sprzed roku.
  const metaKatalogu = catalog.data?.catalog_sync?.source_meta ?? null;

  // SIEROTY: wpis wskazuje na restaurację, której nie da się rozwiązać w słowniku. To stan
  // ZŁĄCZENIA, nie cecha oferty — i dopóki słownik restauracji był pusty, dotyczył wszystkich 13.
  const sierotyKatalogu = daniaKatalogu
    ? daniaKatalogu.filter((d) => d.restaurant_id !== null && !d.restaurant_name).length
    : null;

  // UZGODNIENIE liczone NA EKRANIE, a nie zadeklarowane w dokumencie: ile tożsamości adapter
  // zobaczył w źródle kontra ile katalog ich dziś oddaje. Rozjazd znaczy, że coś przepadło
  // między odczytem a zapisem — i właściciel ma prawo zobaczyć to bez czytania raportu.
  const uzgodnienieKatalogu =
    metaKatalogu == null || catalog.data == null
      ? 'nie do sprawdzenia — brak zapisanej proweniencji odczytu'
      : metaKatalogu.unikalnych_dan === catalog.data.count
        ? `ZGODNE — ${formatCount(metaKatalogu.unikalnych_dan)} tożsamości w źródle = ${formatCount(catalog.data.count)} wpisów w katalogu`
        : `ROZJAZD — źródło ${formatCount(metaKatalogu.unikalnych_dan)}, katalog ${formatCount(catalog.data.count)}`;

  const ziarnoKatalogu = { wierszy: catalog.data?.count ?? null, bytow: restauracjeWKatalogu };
  const katalogPusty = czyPustyZbior(ziarnoKatalogu);

  let stanKatalogu: StanOdczytu = 'CZEKA';
  if (catalog.error) stanKatalogu = 'BLAD';
  else if (catalog.data) stanKatalogu = katalogPusty || catalog.data.count === 0 ? 'PUSTY_ZBIOR' : 'MA_DANE';

  let jakoscKatalogu: DataQualityAssessment = { quality: 'UNKNOWN', reason: 'Odczyt w toku.' };
  if (catalog.error) jakoscKatalogu = { quality: 'UNKNOWN', reason: describeApiError(catalog.error) };
  else if (catalog.data && catalog.data.count === 0)
    jakoscKatalogu = { quality: 'MISSING_HISTORY', reason: 'Katalog nie zwrócił ani jednego dania.' };
  else if (catalog.data)
    jakoscKatalogu = {
      quality: 'PARTIAL',
      reason:
        `Migawka bieżącej oferty: ${formatCount(catalog.data.count)} dań, w tym ` +
        `${formatCount(catalog.data.without_sales_count)} bez sprzedaży. Dania wycofane przed ostatnim odczytem nie występują.`,
    };

  const dowodKatalogu: Dowod = {
    tytul: 'Katalog dań — data ostatniego widzenia',
    etykietaWartosci: 'Najpóźniejsze last_seen_at we wpisach',
    wartosc: ostatniOdczytKatalogu,
    podstawa: { value: 'LIST_VALUE', volume: 'ORDER_ITEM_ROWS' },
    zrodlo: `CRM ${api.baseFor('crm')} → /api/crm/dictionary/dishes${catalog.data ? ` (${catalog.data.source})` : ''}`,
    klasyfikacja: klasyfikacjaKatalogu(metaKatalogu),
    swiezosc: swiezoscKatalogu(metaKatalogu, catalog.data?.catalog_sync?.synced_at ?? null),
    oknoBiezace: 'rejestr — niezależny od okna analizy',
    wyliczenie: 'Największa wartość last_seen_at spośród wpisów katalogu, sprowadzona do daty w czasie warszawskim.',
    szczegoly: [
      ['Stan odczytu', OPIS_STANU[stanKatalogu]],
      ['Dania w katalogu', formatCount(catalog.data?.count ?? null)],
      ['Bez sprzedaży', formatCount(catalog.data?.without_sales_count ?? null)],
      ['Restauracje we wpisach', formatCount(restauracjeWKatalogu)],
      ['Adapter wpisów', zrodlaWpisow.length ? zrodlaWpisow.join(', ') : 'nieodczytany'],
      ['Data karty w źródle', metaKatalogu ? opiszDatyKarty(metaKatalogu.data_karty) : 'brak zapisu odczytu'],
      ['Surowe wiersze menu → tożsamości', metaKatalogu
        ? `${formatCount(metaKatalogu.surowe_wystapienia_menu)} → ${formatCount(metaKatalogu.unikalnych_dan)}`
        : 'nieznane'],
      ['Reguła deduplikacji', metaKatalogu ? metaKatalogu.regula_deduplikacji : 'meal_tuszama_id'],
      ['Uzgodnienie źródło → katalog', uzgodnienieKatalogu],
      ['Wpisy bez rozwiązanej restauracji', sierotyKatalogu === null ? 'nieznane' : formatCount(sierotyKatalogu)],
      ['Szkoły: w źródle / z menu / nieodczytane', metaKatalogu
        ? `${formatCount(metaKatalogu.szkol_w_zrodle)} / ${formatCount(metaKatalogu.szkol_z_menu)} / ${formatCount(metaKatalogu.szkol_nieodczytanych)}`
        : 'nieznane'],
    ],
    proba: `${formatCount(catalog.data?.count ?? null)} wpisów katalogu`,
    jakoscDanych: jakoscKatalogu.reason,
    ograniczenia: [
      'Publiczny katalog Main TEST oddaje kartę NA DZIŚ i tylko dla szkół, które mają dziś menu — zbudowany z niego ' +
        'katalog jest migawką oferty, a nie pełną historią.',
      'Danie wycofane przed ostatnim odczytem w tym zbiorze nie wystąpi.',
    ],
    czegoNieWynika:
      'Z daty ostatniego widzenia NIE wynika, że danie jest dziś w sprzedaży — last_seen_at mówi wyłącznie, kiedy ' +
      'adapter ostatni raz zobaczył je w publicznej karcie. Nie wynika z niej też nic o świeżości sprzedaży: katalog ' +
      'i sprzedaż są zasilane osobno i mogą pochodzić z zupełnie różnych momentów.',
  };

  // ── 4. SZKOŁY (słownik) ─────────────────────────────────────────────────────
  const opcjeSzkol = schools.data?.school_options;
  const miastaSzkol = opcjeSzkol
    ? new Set(opcjeSzkol.map((s) => s.city).filter((c): c is string => Boolean(c))).size
    : null;

  let stanSzkol: StanOdczytu = 'CZEKA';
  if (schools.error) stanSzkol = 'BLAD';
  else if (schools.data && opcjeSzkol === undefined) stanSzkol = 'BRAK_POLA';
  else if (opcjeSzkol) stanSzkol = czyPustyZbior({ wierszy: opcjeSzkol.length, bytow: miastaSzkol }) ? 'PUSTY_ZBIOR' : 'MA_DANE';

  let jakoscSzkol: DataQualityAssessment = { quality: 'UNKNOWN', reason: 'Odczyt w toku.' };
  if (schools.error) jakoscSzkol = { quality: 'UNKNOWN', reason: describeApiError(schools.error) };
  else if (stanSzkol === 'BRAK_POLA')
    jakoscSzkol = { quality: 'UNKNOWN', reason: 'Odpowiedź CRM nie zawiera listy szkół (pole school_options).' };
  else if (raportZlaczenia)
    // Dopasowanie kluczy ze stroną dostaw jest MOCNIEJSZYM sygnałem niż sama liczebność słownika:
    // słownik może być pełny i mimo to nie łączyć się z niczym. Ale gdy jedna ze stron jest pusta,
    // złączenie nie jest „niepewne" — ono się w ogóle nie odbyło, i to jest UNKNOWN, nie usterka kluczy.
    jakoscSzkol = {
      quality:
        brakStronySprzedazy || brakStronyDostaw
          ? 'UNKNOWN'
          : raportZlaczenia.status === 'READY'
            ? 'GOOD'
            : raportZlaczenia.status === 'PARTIAL'
              ? 'PARTIAL'
              : 'UNRELIABLE_JOIN',
      reason: powodZlaczenia ?? raportZlaczenia.reason,
    };
  else if (opcjeSzkol) jakoscSzkol = assessSample({ sampleSize: opcjeSzkol.length, minSample: 1 });

  const dowodSzkol: Dowod = {
    tytul: 'Słownik szkół — pokrycie i dopasowanie kluczy',
    etykietaWartosci: 'Szkoły w słowniku',
    wartosc: opcjeSzkol ? formatCount(opcjeSzkol.length) : null,
    zrodlo: `CRM ${api.baseFor('crm')} → /api/crm/analytics/school-sales (school_options)`,
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — ta sama odpowiedź co sprzedaż, bez znacznika odczytu',
    oknoBiezace: 'słownik — lista niezależna od okna i od sprzedaży',
    wyliczenie: 'Liczba pozycji listy school_options: komplet aktywnych szkół, niezależny od filtra i od sprzedaży w oknie.',
    szczegoly: [
      ['Stan odczytu', OPIS_STANU[stanSzkol]],
      ['Szkoły ze sprzedażą w oknie', formatCount(schools.data?.schools.length ?? null)],
      ['Miasta w słowniku', formatCount(miastaSzkol)],
      [
        'Dopasowanie do dostaw',
        raportZlaczenia
          ? `${formatCount(raportZlaczenia.matched)} dopasowanych · ${formatCount(raportZlaczenia.sales_only)} tylko sprzedaż · ${formatCount(raportZlaczenia.delivery_only)} tylko dostawy`
          : 'nie policzono — wymaga sesji systemu kierowców',
      ],
      [
        'Dostawy bez identyfikatora szkoły',
        raportZlaczenia ? formatCount(raportZlaczenia.delivery_rows_without_id) : 'nie policzono',
      ],
    ],
    proba: opcjeSzkol ? `${formatCount(opcjeSzkol.length)} szkół` : 'brak odczytu',
    jakoscDanych: jakoscSzkol.reason,
    ograniczenia: [
      'Słownik mówi o stanie bazy CRM, a nie o stanie systemu głównego — szkoła dodana w Main pojawi się tu dopiero ' +
        'po synchronizacji, której momentu to źródło nie raportuje.',
      ...(brakStronySprzedazy || brakStronyDostaw
        ? [
            'Dopasowania kluczy NIE da się w tym oknie policzyć: jedna ze stron złączenia nie zwróciła ani jednej ' +
              'szkoły. Zero dopasowanych szkół nie znaczy wtedy, że sprzedaż i dostawy dotyczą rozłącznych szkół.',
          ]
        : []),
    ],
    czegoNieWynika:
      'Z liczby szkół w słowniku NIE wynika, że każda z nich ma dziś aktywne menu ani że którakolwiek cokolwiek ' +
      'sprzedała. Nie wynika z niej również, że słownik jest aktualny: lista jest kompletna wobec stanu bazy CRM, ' +
      'a moment tego stanu pozostaje nieustalony.',
  };

  // ── 5. RESTAURACJE (słownik) ────────────────────────────────────────────────
  const opcjeRestauracji = restaurants.data?.restaurant_options;
  const miastaRestauracji = opcjeRestauracji
    ? new Set(opcjeRestauracji.map((r) => r.city).filter((c): c is string => Boolean(c))).size
    : null;

  let stanRestauracji: StanOdczytu = 'CZEKA';
  if (restaurants.error) stanRestauracji = 'BLAD';
  else if (restaurants.data && opcjeRestauracji === undefined) stanRestauracji = 'BRAK_POLA';
  else if (opcjeRestauracji)
    stanRestauracji = czyPustyZbior({ wierszy: opcjeRestauracji.length, bytow: miastaRestauracji }) ? 'PUSTY_ZBIOR' : 'MA_DANE';

  let jakoscRestauracji: DataQualityAssessment = { quality: 'UNKNOWN', reason: 'Odczyt w toku.' };
  if (restaurants.error) jakoscRestauracji = { quality: 'UNKNOWN', reason: describeApiError(restaurants.error) };
  else if (stanRestauracji === 'BRAK_POLA')
    jakoscRestauracji = { quality: 'UNKNOWN', reason: 'Odpowiedź CRM nie zawiera listy restauracji (pole restaurant_options).' };
  else if (opcjeRestauracji) jakoscRestauracji = assessSample({ sampleSize: opcjeRestauracji.length, minSample: 1 });

  const dowodRestauracji: Dowod = {
    tytul: 'Słownik restauracji — pokrycie',
    etykietaWartosci: 'Restauracje w słowniku',
    wartosc: opcjeRestauracji ? formatCount(opcjeRestauracji.length) : null,
    zrodlo: `CRM ${api.baseFor('crm')} → /api/crm/analytics/restaurant-sales (restaurant_options)`,
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — ta sama odpowiedź co sprzedaż restauracji, bez znacznika odczytu',
    oknoBiezace: 'słownik — lista niezależna od okna i od sprzedaży',
    wyliczenie: 'Liczba pozycji listy restaurant_options: komplet aktywnych restauracji, niezależny od filtra i od sprzedaży.',
    szczegoly: [
      ['Stan odczytu', OPIS_STANU[stanRestauracji]],
      ['Restauracje ze sprzedażą w oknie', formatCount(restaurants.data?.summary.restaurants_count ?? null)],
      ['Miasta w słowniku', formatCount(miastaRestauracji)],
      ['Restauracje obecne w katalogu dań', formatCount(restauracjeWKatalogu)],
    ],
    proba: opcjeRestauracji ? `${formatCount(opcjeRestauracji.length)} restauracji` : 'brak odczytu',
    jakoscDanych: jakoscRestauracji.reason,
    ograniczenia: [
      'Liczba restauracji w słowniku i liczba restauracji obecnych w katalogu dań to DWIE różne wielkości — ' +
        'katalog widzi tylko te, które miały dziś kartę w publicznym katalogu.',
    ],
    czegoNieWynika:
      'Z liczby restauracji w słowniku NIE wynika, ile z nich dziś gotuje ani ile ma aktywną kartę. Różnica między ' +
      'tą liczbą a liczbą restauracji w katalogu dań NIE jest miarą tego, ile restauracji przestało działać — ' +
      'to dwa zbiory zasilane z osobnych źródeł.',
  };

  // -- SLOWNIKI REFERENCYJNE ---------------------------------------------------
  //
  // Osobno od wymiarow sprzedazy o tych samych nazwach. Tamte odpowiadaja na pytanie "gdzie cos
  // kupiono", te - "co w ogole istnieje". Maja inne zrodlo, inna swiezosc i inny bloker.
  const metaSzkol = slownikSzkol.data?.dictionary_sync?.source_meta ?? null;
  const metaRest = slownikRestauracji.data?.dictionary_sync?.source_meta ?? null;
  const teraz = new Date().toISOString();

  const swiezoscSzkolRef = ocenSwiezoscReferencyjna({
    dataKarty: dekodujDateKarty(metaSzkol?.data_karty?.[0] ?? null),
    odczytano: slownikSzkol.data?.dictionary_sync?.synced_at ?? null,
    teraz,
  });
  const swiezoscRestRef = ocenSwiezoscReferencyjna({
    dataKarty: dekodujDateKarty(metaRest?.data_karty?.[0] ?? null),
    odczytano: slownikRestauracji.data?.dictionary_sync?.synced_at ?? null,
    teraz,
  });

  // UZGODNIENIE liczone NA EKRANIE: ile zrodlo widzialo kontra ile slownik dzis oddaje.
  const uzgodnienieSzkol =
    metaSzkol == null || slownikSzkol.data == null
      ? 'nie do sprawdzenia \u2014 brak zapisanej proweniencji odczytu'
      : metaSzkol.szkol_w_zrodle === slownikSzkol.data.count
        ? `ZGODNE \u2014 ${formatCount(metaSzkol.szkol_w_zrodle)} w \u017ar\u00f3dle = ${formatCount(slownikSzkol.data.count)} w s\u0142owniku`
        : `ROZJAZD \u2014 \u017ar\u00f3d\u0142o ${formatCount(metaSzkol.szkol_w_zrodle)}, s\u0142ownik ${formatCount(slownikSzkol.data.count)}`;
  const uzgodnienieRest =
    metaRest == null || slownikRestauracji.data == null
      ? 'nie do sprawdzenia \u2014 brak zapisanej proweniencji odczytu'
      : metaRest.restauracji === slownikRestauracji.data.count
        ? `ZGODNE \u2014 ${formatCount(metaRest.restauracji)} w \u017ar\u00f3dle = ${formatCount(slownikRestauracji.data.count)} w s\u0142owniku`
        : `ROZJAZD \u2014 \u017ar\u00f3d\u0142o ${formatCount(metaRest.restauracji)}, s\u0142ownik ${formatCount(slownikRestauracji.data.count)}`;

  const nieodczytane = metaSzkol?.szkoly_nieodczytane_lista ?? [];
  const duplikatySzkol = slownikSzkol.data
    ? slownikSzkol.data.count - new Set(slownikSzkol.data.schools.map((x) => x.school_id)).size
    : null;
  const duplikatyRest = slownikRestauracji.data
    ? slownikRestauracji.data.count - new Set(slownikRestauracji.data.restaurants.map((x) => x.restaurant_id)).size
    : null;

  let stanSzkolRef: StanOdczytu = 'CZEKA';
  if (slownikSzkol.error) stanSzkolRef = 'BLAD';
  else if (slownikSzkol.data) stanSzkolRef = slownikSzkol.data.count === 0 ? 'PUSTY_ZBIOR' : 'MA_DANE';
  let stanRestRef: StanOdczytu = 'CZEKA';
  if (slownikRestauracji.error) stanRestRef = 'BLAD';
  else if (slownikRestauracji.data) stanRestRef = slownikRestauracji.data.count === 0 ? 'PUSTY_ZBIOR' : 'MA_DANE';

  const dowodSzkolRef: Dowod = {
    tytul: 'S\u0142ownik szk\u00f3\u0142 \u2014 dane referencyjne',
    etykietaWartosci: 'Szk\u00f3\u0142 w s\u0142owniku',
    wartosc: slownikSzkol.data ? formatCount(slownikSzkol.data.count) : null,
    zrodlo: `${metaSzkol?.zrodlo ?? 'main-test-public-catalog'} \u2192 CRM /api/crm/dictionary/schools`,
    klasyfikacja: klasyfikacjaKatalogu(metaSzkol),
    swiezosc: `${OPIS_SWIEZOSCI[swiezoscSzkolRef.stan]} \u2014 ${swiezoscSzkolRef.powod}`,
    oknoBiezace: 's\u0142ownik \u2014 niezale\u017cny od okna analizy',
    wyliczenie:
      'Wszystkie szko\u0142y z publicznego wykazu miast Main TEST (/cities), zapisane w crm_schools po kanonicznym identyfikatorze szko\u0142y.',
    szczegoly: [
      ['Stan odczytu', OPIS_STANU[stanSzkolRef]],
      ['Zakres \u017ar\u00f3d\u0142a', 'wszystkie szko\u0142y widoczne w katalogu publicznym'],
      ['Identyfikator kanoniczny', 'school_tuszama_id'],
      ['Szk\u00f3\u0142 w \u017ar\u00f3dle', metaSzkol ? formatCount(metaSzkol.szkol_w_zrodle) : 'nieznane'],
      ['Szk\u00f3\u0142 w s\u0142owniku CRM', formatCount(slownikSzkol.data?.count ?? null)],
      ['Uzgodnienie \u017ar\u00f3d\u0142o \u2192 s\u0142ownik', uzgodnienieSzkol],
      ['Z kart\u0105 dzi\u015b / bez karty', metaSzkol ? `${formatCount(metaSzkol.szkol_z_menu)} / ${formatCount(metaSzkol.szkol_bez_menu)}` : 'nieznane'],
      ['NIEODCZYTANE (to nie jest \u201ebez karty\u201d)', metaSzkol ? formatCount(metaSzkol.szkol_nieodczytanych) : 'nieznane'],
      ['Kt\u00f3re nieodczytane', nieodczytane.length ? nieodczytane.map((n) => `${n.name ?? '?'} (id ${n.id}, ${n.powod})`).join('; ') : 'brak'],
      ['Duplikaty identyfikatora', duplikatySzkol === null ? 'nieznane' : formatCount(duplikatySzkol)],
      ['Data zawarto\u015bci \u017ar\u00f3d\u0142a', metaSzkol ? opiszDatyKarty(metaSzkol.data_karty) : 'brak zapisu'],
      ['Moment odczytu', slownikSzkol.data?.dictionary_sync?.synced_at ?? 'brak zapisu'],
    ],
    proba: slownikSzkol.data ? `${formatCount(slownikSzkol.data.count)} szk\u00f3\u0142` : 'brak odczytu',
    ograniczenia: [
      'Szko\u0142a NIEODCZYTANA to nie to samo co szko\u0142a BEZ KARTY. Pierwsze jest awari\u0105 odczytu, drugie stanem oferty \u2014 ' +
        'w liczbach wygl\u0105daj\u0105 tak samo, wi\u0119c s\u0105 liczone i wypisywane osobno.',
      'S\u0142ownik obejmuje szko\u0142y widoczne w katalogu PUBLICZNYM. Szko\u0142a istniej\u0105ca w systemie g\u0142\u00f3wnym, ale niewidoczna ' +
        'publicznie, w tym s\u0142owniku nie wyst\u0105pi.',
    ],
    czegoNieWynika:
      'Ze \u015bwie\u017co\u015bci tego s\u0142ownika NIE wynika \u015bwie\u017co\u015b\u0107 danych o SPRZEDA\u017bY w tych szko\u0142ach \u2014 to osobne \u017ar\u00f3d\u0142o ' +
      'i jego \u015bwie\u017co\u015b\u0107 pozostaje NIEUSTALONA. Nie wynika te\u017c, \u017ce szko\u0142a jest aktywnym klientem: obecno\u015b\u0107 w wykazie ' +
      'to nie to samo co zam\u00f3wienia.',
  };

  const dowodRestRef: Dowod = {
    tytul: 'S\u0142ownik restauracji \u2014 dane referencyjne',
    etykietaWartosci: 'Restauracji w s\u0142owniku',
    wartosc: slownikRestauracji.data ? formatCount(slownikRestauracji.data.count) : null,
    zrodlo: `${metaRest?.zrodlo ?? 'main-test-public-catalog'} \u2192 CRM /api/crm/dictionary/restaurants`,
    klasyfikacja: klasyfikacjaKatalogu(metaRest),
    swiezosc: `${OPIS_SWIEZOSCI[swiezoscRestRef.stan]} \u2014 ${swiezoscRestRef.powod}`,
    oknoBiezace: 's\u0142ownik \u2014 niezale\u017cny od okna analizy',
    wyliczenie:
      'Restauracje wyst\u0119puj\u0105ce w kartach odczytanych z publicznego katalogu Main TEST, zapisane po kanonicznym identyfikatorze restauracji.',
    szczegoly: [
      ['Stan odczytu', OPIS_STANU[stanRestRef]],
      ['ZAKRES \u0179R\u00d3D\u0141A', 'wy\u0142\u0105cznie restauracje z karty z dnia odczytu \u2014 NIE pe\u0142ny rejestr Main'],
      ['Identyfikator kanoniczny', 'restaurant_tuszama_id'],
      ['Restauracji w \u017ar\u00f3dle', metaRest ? formatCount(metaRest.restauracji) : 'nieznane'],
      ['Restauracji w s\u0142owniku CRM', formatCount(slownikRestauracji.data?.count ?? null)],
      ['Uzgodnienie \u017ar\u00f3d\u0142o \u2192 s\u0142ownik', uzgodnienieRest],
      ['Duplikaty identyfikatora', duplikatyRest === null ? 'nieznane' : formatCount(duplikatyRest)],
      ['Data zawarto\u015bci \u017ar\u00f3d\u0142a', metaRest ? opiszDatyKarty(metaRest.data_karty) : 'brak zapisu'],
      ['Moment odczytu', slownikRestauracji.data?.dictionary_sync?.synced_at ?? 'brak zapisu'],
    ],
    proba: slownikRestauracji.data ? `${formatCount(slownikRestauracji.data.count)} restauracji` : 'brak odczytu',
    ograniczenia: [
      'ZAKRES JEST CZ\u0118\u015aCIOWY I TO JEST ZMIERZONE, NIE OSTRO\u017bNO\u015a\u0106: publiczne API Main TEST nie ma \u017cadnego wykazu ' +
        'restauracji (/restaurants, /schools, /meals odpowiadaj\u0105 404). Jedyne, co da si\u0119 z niego wyczyta\u0107, to ' +
        'restauracje obecne w kartach szk\u00f3\u0142. Ta liczba NIE JEST liczb\u0105 restauracji wsp\u00f3\u0142pracuj\u0105cych z TuSzam\u0105.',
    ],
    czegoNieWynika:
      'Z tej liczby NIE wynika, ile restauracji ma TuSzama \u2014 wynika tylko, ile z nich mia\u0142o kart\u0119 w dniu odczytu. ' +
      'Nie wynika te\u017c nic o sprzeda\u017cy: to s\u0142ownik, nie wolumen.',
  };

  // ── Tabela źródeł ───────────────────────────────────────────────────────────
  const srodowisko = config.environment;
  const zrodla: readonly WierszZrodla[] = [
    {
      klucz: 'sprzedaz',
      kartaId: 'DATA_QUALITY-11',
      grupa: 'SPRZEDAZ',
      nazwa: 'Transakcje',
      czym: 'transakcje i pozycje zamówień — podstawa wszystkich liczb sprzedażowych',
      srodowisko: `CRM · ${srodowisko}`,
      klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
      swiezosc: 'NIEUSTALONA — źródło nie raportuje momentu odczytu',
      swiezoscUstalona: false,
      ostatniaData: ostatniDzienSprzedazy,
      oDacie: 'najpóźniejszy dzień ze sprzedażą W OKNIE (oś: dzień dostawy)',
      jakosc: jakoscSprzedazy,
      bloker:
        stanSprzedazy === 'BLAD'
          ? `Odczyt nie powiódł się: ${jakoscSprzedazy.reason} Świeżość źródła pozostaje nieustalona — to największa otwarta luka programu.`
          : 'Świeżość nieustalona — największa otwarta luka programu. Do rozstrzygnięcia: znacznik synchronizacji w odpowiedzi CRM.',
      stan: stanSprzedazy,
      podstawa: { value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' },
      dowod: dowodSprzedazy,
    },
    {
      klucz: 'dostawy',
      kartaId: 'DATA_QUALITY-17',
      grupa: 'DOSTAWY',
      nazwa: 'Dostawy Drive',
      czym: 'utrwalone statusy dostaw z aplikacji kierowców',
      srodowisko: `Drive · ${srodowisko}`,
      klasyfikacja: klasyfikacjaDrive(),
      swiezosc: driveConnected ? `odczyt bieżący (${swiezoscDrive()})` : 'nieustalona — nie było odczytu',
      swiezoscUstalona: driveConnected && Boolean(delivery.data),
      ostatniaData: ostatniDzienDostawy,
      oDacie:
        driveConnected && trend.isLoading
          ? 'odczyt dziennej serii dostaw w toku'
          : 'najpóźniejszy dzień z dostawą W OKNIE',
      jakosc: jakoscDostaw,
      bloker: driveConnected
        ? 'Zbiór kontrolowany: realne API i schemat, dane wprowadzone do weryfikacji — nie wolno czytać wolumenu jak produkcji.'
        : 'Wymaga podłączenia systemu kierowców — bez sesji nie ma odczytu, więc nie ma czego oceniać.',
      stan: stanDostaw,
      podstawa: { time: 'DELIVERY_DATE' },
      dowod: dowodDostaw,
    },
    {
      klucz: 'katalog',
      kartaId: 'DATA_QUALITY-14',
      grupa: 'REFERENCJA',
      nazwa: 'Katalog dań',
      czym: 'rejestr oferty czytany z publicznego katalogu Main TEST — zawiera dania bez sprzedaży',
      srodowisko: `Main (katalog publiczny) · ${srodowisko}`,
      klasyfikacja: klasyfikacjaKatalogu(metaKatalogu),
      swiezosc: swiezoscKatalogu(metaKatalogu, catalog.data?.catalog_sync?.synced_at ?? null),
      swiezoscUstalona: metaKatalogu != null && (metaKatalogu.data_karty?.length ?? 0) > 0,
      ostatniaData: ostatniOdczytKatalogu,
      oDacie: 'najpóźniejsze last_seen_at wpisu katalogu',
      jakosc: jakoscKatalogu,
      bloker:
        sierotyKatalogu
          ? `${formatCount(sierotyKatalogu)} wpisów wskazuje na restaurację, której nie ma w słowniku — to stan złączenia, nie cecha oferty.`
          : 'Migawka bieżącej oferty — dania wycofane przed ostatnim odczytem w katalogu nie występują. Oferta ≠ sprzedaż: świeżość karty nic nie mówi o świeżości transakcji.',
      stan: stanKatalogu,
      podstawa: { value: 'LIST_VALUE', volume: 'ORDER_ITEM_ROWS' },
      dowod: dowodKatalogu,
    },
    {
      klucz: 'szkoly-ref',
      kartaId: 'DATA_QUALITY-15',
      grupa: 'REFERENCJA',
      nazwa: 'Szko\u0142y \u2014 s\u0142ownik',
      czym: 'jakie szko\u0142y w og\u00f3le istniej\u0105 w katalogu publicznym Main TEST',
      srodowisko: `Main (katalog publiczny) \u00b7 ${srodowisko}`,
      klasyfikacja: klasyfikacjaKatalogu(metaSzkol),
      swiezosc: `${OPIS_SWIEZOSCI[swiezoscSzkolRef.stan]} \u2014 ${swiezoscSzkolRef.powod}`,
      swiezoscUstalona: swiezoscSzkolRef.stan !== 'UNKNOWN_REFERENCE',
      ostatniaData: metaSzkol ? opiszDatyKarty(metaSzkol.data_karty) : null,
      oDacie: 'data zawarto\u015bci, jak\u0105 odda\u0142o \u017ar\u00f3d\u0142o przy odczycie',
      jakosc: slownikSzkol.error
        ? { quality: 'UNKNOWN', reason: describeApiError(slownikSzkol.error) }
        : slownikSzkol.data?.count
          ? { quality: 'GOOD', reason: `${formatCount(slownikSzkol.data.count)} szk\u00f3\u0142, uzgodnienie ze \u017ar\u00f3d\u0142em bez ubytk\u00f3w.` }
          : { quality: 'MISSING_HISTORY', reason: 'S\u0142ownik szk\u00f3\u0142 jest pusty.' },
      bloker: nieodczytane.length
        ? `${formatCount(nieodczytane.length)} szk\u00f3\u0142 NIEODCZYTANYCH \u2014 to nie to samo co \u201ebez karty\u201d. Wypisane imiennie w dowodzie.`
        : null,
      stan: stanSzkolRef,
      dowod: dowodSzkolRef,
    },
    {
      klucz: 'restauracje-ref',
      kartaId: 'DATA_QUALITY-16',
      grupa: 'REFERENCJA',
      nazwa: 'Restauracje \u2014 s\u0142ownik (zakres cz\u0119\u015bciowy)',
      czym: 'restauracje obecne w karcie z dnia odczytu \u2014 NIE pe\u0142ny rejestr Main',
      srodowisko: `Main (katalog publiczny) \u00b7 ${srodowisko}`,
      klasyfikacja: klasyfikacjaKatalogu(metaRest),
      swiezosc: `${OPIS_SWIEZOSCI[swiezoscRestRef.stan]} \u2014 ${swiezoscRestRef.powod}`,
      swiezoscUstalona: swiezoscRestRef.stan !== 'UNKNOWN_REFERENCE',
      ostatniaData: metaRest ? opiszDatyKarty(metaRest.data_karty) : null,
      oDacie: 'data zawarto\u015bci, jak\u0105 odda\u0142o \u017ar\u00f3d\u0142o przy odczycie',
      jakosc: slownikRestauracji.error
        ? { quality: 'UNKNOWN', reason: describeApiError(slownikRestauracji.error) }
        : slownikRestauracji.data?.count
          ? { quality: 'PARTIAL', reason: 'Zakres cz\u0119\u015bciowy: wy\u0142\u0105cznie restauracje z karty z dnia odczytu.' }
          : { quality: 'MISSING_HISTORY', reason: 'S\u0142ownik restauracji jest pusty.' },
      bloker:
        'Publiczne API Main TEST nie ma wykazu restauracji \u2014 ta liczba NIE jest liczb\u0105 restauracji wsp\u00f3\u0142pracuj\u0105cych z TuSzam\u0105.',
      stan: stanRestRef,
      dowod: dowodRestRef,
    },
    {
      klucz: 'szkoly',
      kartaId: 'DATA_QUALITY-12',
      // Raport złączenia (matched / sales_only / delivery_only / delivery_rows_without_id)
      // jest w inwentarzu osobną kartą, a renderuje się wyłącznie w dowodzie tego wiersza.
      dowodId: 'DATA_QUALITY-18',
      grupa: 'SPRZEDAZ',
      nazwa: 'Szkoły — wymiar sprzedaży',
      czym: 'w JAKICH szkołach coś kupiono — wymiar odpowiedzi sprzedażowej, NIE słownik szkół',
      srodowisko: `CRM · ${srodowisko}`,
      klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
      swiezosc: 'NIEUSTALONA — słownik nie niesie znacznika czasu',
      swiezoscUstalona: false,
      ostatniaData: null,
      oDacie: 'źródło nie raportuje żadnej daty',
      jakosc: jakoscSzkol,
      bloker:
        raportZlaczenia && raportZlaczenia.status === 'BLOCKED'
          ? brakStronySprzedazy || brakStronyDostaw
            ? `Złączenia ze stroną dostaw nie dało się policzyć: ${powodZlaczenia}`
            : `Klucze nie łączą się z dostawami: ${powodZlaczenia}`
          : 'Świeżość nieustalona — ta sama odpowiedź co sprzedaż, bez znacznika odczytu.',
      stan: stanSzkol,
      dowod: dowodSzkol,
    },
    {
      klucz: 'restauracje',
      kartaId: 'DATA_QUALITY-13',
      grupa: 'SPRZEDAZ',
      nazwa: 'Restauracje — wymiar sprzedaży',
      czym: 'w JAKICH restauracjach coś kupiono — wymiar odpowiedzi sprzedażowej, NIE słownik restauracji',
      srodowisko: `CRM · ${srodowisko}`,
      klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
      swiezosc: 'NIEUSTALONA — słownik nie niesie znacznika czasu',
      swiezoscUstalona: false,
      ostatniaData: null,
      oDacie: 'źródło nie raportuje żadnej daty',
      jakosc: jakoscRestauracji,
      bloker: 'Świeżość nieustalona — ta sama odpowiedź co sprzedaż restauracji, bez znacznika odczytu.',
      stan: stanRestauracji,
      dowod: dowodRestauracji,
    },
  ];

  const zUstalonaSwiezoscia = zrodla.filter((z) => z.swiezoscUstalona).length;
  const policz = (stan: StanOdczytu) => zrodla.filter((z) => z.stan === stan).length;
  const zDanymi = policz('MA_DANE');
  const pustych = policz('PUSTY_ZBIOR');
  const niepodlaczonych = policz('NIEPODLACZONE');
  const bledow = policz('BLAD') + policz('BRAK_POLA');
  // Piąty odczyt jest celowo pokazany: pięć wskazań MUSI sumować się do liczby źródeł.
  // Przyrząd, którego wskazania nie domykają się do całości, nie daje się sprawdzić.
  const wTrakcie = policz('CZEKA');

  const dowodGotowosci: Dowod = {
    tytul: 'Źródła z ustaloną świeżością',
    etykietaWartosci: 'Źródła, dla których znamy moment pochodzenia danych',
    wartosc: `${zUstalonaSwiezoscia} z ${zrodla.length}`,
    zrodlo: 'Control Center — stan odczytu pięciu źródeł tego ekranu (bez osobnego zapytania)',
    // Agregat dziedziczy najsłabsze ogniwo. Nazwanie go inaczej niż najgorszy składnik byłoby
    // zawyżeniem wiarygodności całości.
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'bieżąca — licznik liczony przy każdym renderze z odpowiedzi API, nie z zapisanego stanu',
    oknoBiezace: okno,
    oknoPorownawcze,
    wyliczenie:
      'Liczba źródeł, dla których dało się ustalić moment pochodzenia danych, przez liczbę źródeł w tabeli poniżej.',
    szczegoly: zrodla.map((z) => [z.nazwa, z.swiezoscUstalona ? 'świeżość ustalona' : 'świeżość NIEUSTALONA'] as const),
    proba: `${zrodla.length} źródeł`,
    jakoscDanych: `${zDanymi} z danymi · ${pustych} pusty zbiór · ${niepodlaczonych} bez sesji · ${bledow} bez odczytu`,
    ograniczenia: [
      'Licznik mówi o ŹRÓDŁACH, nie o rekordach: źródło z ustaloną świeżością i pustym zbiorem nadal liczy się jako ustalone.',
      'Ustalenie świeżości sprawdzamy tam, gdzie źródło samo podaje znacznik (last_seen_at) albo gdzie odczyt jest ' +
        'wykonywany na żywo. Nie wnioskujemy jej z treści danych.',
    ],
    czegoNieWynika:
      'Nie wynika z tego, że dane w tych źródłach są poprawne ani kompletne — ustalona świeżość mówi wyłącznie, ' +
      'KIEDY dane powstały, a nie czy są prawdziwe. Nie wynika też, że pozostałe źródła są nieaktualne: ' +
      '„nieustalona" znaczy „nie wiadomo", a nie „stare".',
  };

  // ── Rodowód wydania ─────────────────────────────────────────────────────────
  // Porównujemy identyfikatory WPIECZONE w artefakt z tym, czego klient realnie używa. Rozjazd
  // znaczy, że publiczna akceptacja E2E dotyczy innego artefaktu niż ten, który właśnie działa.
  const rozjazdy: { pole: string; wArtefakcie: string; wKliencie: string }[] = [];
  if (buildInfo.environment !== config.environment) {
    rozjazdy.push({ pole: 'Środowisko', wArtefakcie: buildInfo.environment, wKliencie: config.environment });
  }
  // JEDEN ADRES zamiast trzech. Przeglądarka rozmawia wyłącznie z backendem Control Center,
  // więc porównujemy jedno wejście — a nie trzy adresy źródeł, których front już nie zna.
  {
    const wpieczony = buildInfo.api.cc;
    const uzywany = api.baseFor('crm') || '(to samo pochodzenie)';
    if (wpieczony !== uzywany) {
      rozjazdy.push({ pole: 'Adres Control Center', wArtefakcie: wpieczony, wKliencie: uzywany });
    }
  }
  // Prefiks ścieżki jest jedynym identyfikatorem, który da się skonfrontować z RZECZYWISTOŚCIĄ
  // przeglądarki, a nie tylko z inną wartością z tego samego builda. Artefakt zbudowany pod inny
  // prefiks niż ten, spod którego realnie się załadował, wygląda zdrowo i nie działa.
  if (typeof window !== 'undefined') {
    const sciezka = window.location.pathname.endsWith('/') ? window.location.pathname : `${window.location.pathname}/`;
    if (!sciezka.startsWith(buildInfo.base)) {
      rozjazdy.push({ pole: 'Prefiks ścieżki', wArtefakcie: buildInfo.base, wKliencie: window.location.pathname });
    }
  }

  const nieprzypisywalny = buildInfo.gitDirty || buildInfo.gitSha === 'unknown';
  const dowodHistoryczny = rozjazdy.length > 0 || nieprzypisywalny;
  const rozjazdDla = (pole: string) => {
    const r = rozjazdy.find((x) => x.pole === pole);
    return r ? `klient używa: ${r.wKliencie}` : null;
  };

  // ── Wymaga uwagi ────────────────────────────────────────────────────────────
  // Wyłącznie z tego, co realnie widać w odczycie. Żadnych dopisanych szczegółów.
  const uwagi: PozycjaUwagi[] = [];
  if (stanSprzedazy === 'PUSTY_ZBIOR') {
    uwagi.push({
      waga: 'krytyczna',
      tytul: 'Źródło sprzedaży zwróciło pusty zbiór',
      detal: 'Zero zamówień i zero szkół w tym oknie — to stan zasilania, nie wynik sprzedaży',
      cel: '/crm',
    });
  }
  if (dowodHistoryczny) {
    uwagi.push({
      waga: 'krytyczna',
      tytul: 'Artefakt nie zgadza się z dowodem z testu',
      detal: nieprzypisywalny
        ? 'Buildu nie da się jednoznacznie powiązać z commitem'
        : `Rozjazd identyfikatorów: ${rozjazdy.map((r) => r.pole).join(', ')}`,
      cel: '/system',
    });
  }
  // Pozycję o kluczach wystawiamy WYŁĄCZNIE wtedy, gdy obie strony coś zwróciły. Przy pustej
  // stronie nie ma usterki złączenia — jest brak danych, i mówi o tym osobna pozycja wyżej.
  if (raportZlaczenia && raportZlaczenia.status === 'BLOCKED' && !brakStronySprzedazy && !brakStronyDostaw) {
    uwagi.push({
      waga: 'krytyczna',
      tytul: 'Klucze szkół nie łączą się z dostawami',
      detal: powodZlaczenia ?? raportZlaczenia.reason,
      cel: '/insights',
    });
  }
  uwagi.push({
    waga: 'uwaga',
    tytul: 'Świeżość źródła sprzedaży nieustalona',
    detal: 'Nie wiadomo, z którego momentu pochodzą liczby sprzedaży — to nie to samo co brak sprzedaży',
    cel: '/crm',
  });
  if (!driveConnected) {
    uwagi.push({
      waga: 'uwaga',
      tytul: 'Dostawy niepodłączone',
      detal: 'Bez sesji systemu kierowców nie ma odczytu, więc nie ma czego mierzyć',
      cel: '/drive',
    });
  }
  if (stanKatalogu === 'PUSTY_ZBIOR') {
    uwagi.push({
      waga: 'uwaga',
      tytul: 'Katalog dań nie zwrócił żadnego wpisu',
      detal: 'Bez katalogu nie da się zapytać, czego oferta nie sprzedaje',
      cel: '/restauracje/katalog',
    });
  }

  return (
    <Box>
      <NaglowekEkranu
        tytul="Jakość danych"
        opis="Ten ekran odpowiada na jedno pytanie: czy na liczbach z pozostałych ekranów wolno oprzeć decyzję — a jeżeli nie, to które źródło jest słabym ogniwem i co konkretnie jest w nim nieustalone."
        akcja={<DateRangeFilter wariant="naglowek" comparison={dishes.data?.range.comparison} kartaId="DATA_QUALITY-10" />}
      />

      <PasSekcji wariant="cichy" tytul="Kontrolowane vs realne" />

      {/*
        Skąd te dane pochodzą, zanim padnie jakakolwiek liczba. Zbiór KONTROLOWANY (wprowadzony
        do weryfikacji) i zbiór PRODUKCYJNY dają identycznie wyglądające odpowiedzi — a wolumen
        z pierwszego przeczytany jak z drugiego jest zdaniem o firmie wyciągniętym ze stanu
        środowiska. Znacznik stoi więc nad wszystkim, co opisuje.
      */}
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }}>
        <Chip size="small" variant="outlined" label={describeDataOrigin(config.dataOrigin)} />
        <Chip size="small" variant="outlined" label={`środowisko: ${config.environment}`} />
        <Chip
          size="small"
          variant="outlined"
          label={config.readOnly ? 'tylko odczyt (zapis zablokowany w kliencie)' : 'zapis dozwolony'}
        />
      </Stack>

      <Stack spacing={3} className="sygnal-kaskada">
        <PlytaGlowna
          etykieta="Źródła z ustaloną świeżością"
          wartosc={`${zUstalonaSwiezoscia} z ${zrodla.length}`}
          opis={
            <Typography variant="body2">
              licznik mówi o źródłach, nie o rekordach — „nieustalona" znaczy „nie wiadomo, z którego momentu",
              a nie „stare"
            </Typography>
          }
          zrodlo="Control Center — stan odczytu pięciu źródeł tego ekranu"
          okno={okno}
          dowod={dowodGotowosci}
          boczne={
            <>
              <MiniMetryka wartosc={formatCount(zDanymi)} etykieta="źródeł z danymi" />
              <MiniMetryka wartosc={formatCount(pustych)} etykieta="pusty zbiór" />
              <MiniMetryka wartosc={formatCount(niepodlaczonych)} etykieta="bez sesji" />
              <MiniMetryka wartosc={formatCount(bledow)} etykieta="bez odczytu" />
              <MiniMetryka wartosc={formatCount(wTrakcie)} etykieta="odczyt w toku" />
            </>
          }
        />

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '7fr 5fr' }, alignItems: 'start' }}>
          <Card>
            <CardContent>
              <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
                Rozróżnienie, bez którego cała tabela poniżej znaczy co innego
              </Typography>
              <Typography variant="h5" sx={{ mb: 0.5 }}>
                Trzy zdania, które w API wyglądają tak samo
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--tekst-2)', mb: 2 }}>
                Suma z pustego zbioru wynosi 0, więc odpowiedź API jest identyczna niezależnie od tego, czy nie było
                czego liczyć, czy policzono i wyszło zero. Rozstrzyga liczba wierszy, nie wartość.
              </Typography>

              <Stack spacing={2}>
                <Rozroznienie
                  barwa="var(--uwaga)"
                  tytul="Świeżość nieustalona"
                  rodzaj="sygnał techniczny"
                  tresc="Źródło odpowiada, ale nie mówi, z którego momentu pochodzi jego zawartość. Nie wiadomo, czy patrzymy na wczoraj, czy na kopię sprzed miesięcy — i tego nie da się poznać po samych liczbach."
                />
                <Rozroznienie
                  barwa="var(--krytyczny)"
                  tytul="Pusty zbiór"
                  rodzaj="sygnał techniczny"
                  tresc="Zero wierszy do policzenia. Sprzedaż mogła być — po prostu nie ma jej w tym źródle. Zajmuje się tym zasilanie, nie handel."
                />
                <Rozroznienie
                  barwa="var(--sukces)"
                  tytul="Brak sprzedaży"
                  rodzaj="sygnał biznesowy"
                  tresc="Źródło zwróciło komplet wierszy, a wynik wynosi zero. To jest ZMIERZONE zero — dopiero po nim ktoś powinien zadzwonić."
                />
              </Stack>

              <Alert severity="warning" icon={false} sx={{ mt: 2, borderRadius: 'var(--r-maly)' }}>
                <Typography variant="body2">
                  Pomylenie dwóch pierwszych z trzecim prowadzi wprost do fałszywego wniosku „nic się nie sprzedaje" —
                  czyli do zdania o firmie wyciągniętego ze stanu integracji. Ten ekran istnieje po to, żeby ta pomyłka
                  nie zależała od tego, czy ktoś akurat o niej pamiętał.
                </Typography>
              </Alert>
            </CardContent>
          </Card>

          <WymagaUwagi pozycje={uwagi} />
        </Box>

        {/*
          PIĘĆ OBSZARÓW Z MAKIETY, w jej kolejności: świeżość → kontrolowane vs realne →
          braki pól → pokrycie złączeń → rodowód wydania. Ekran miał całą tę treść, ale
          rozłożoną bez nazwanych działów, więc czytelnik nie wiedział, na które z pięciu
          pytań akurat patrzy. Dzieli się to, co już jest — nic nie jest tu przenoszone
          ani przepisywane.
        */}
        <PasSekcji wariant="cichy" tytul="Świeżość źródeł" />

        <PasSekcji tytul="Rejestr odczytów" />

        <Karta nadtytul="Jedno źródło = jeden wiersz" tytul="Źródła danych">
          <Typography variant="body2" sx={{ color: 'var(--tekst-2)', mb: 1.5, maxWidth: '78ch' }}>
            Stan każdego wiersza pochodzi z realnego odczytu wykonanego w tej chwili — nie z listy wpisanej w kod.
            Kolumna „ostatnia znana data" mówi, jaka data jest widoczna W DANYCH; kolumna „świeżość" mówi, czy źródło
            w ogóle potrafi podać swój własny moment. To są dwa różne pytania.
          </Typography>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Źródła danych">
              <TableHead>
                <TableRow>
                  <TableCell>Źródło</TableCell>
                  <TableCell>Środowisko</TableCell>
                  <TableCell>Klasyfikacja</TableCell>
                  <TableCell>Świeżość</TableCell>
                  <TableCell>Ostatnia znana data</TableCell>
                  <TableCell>Jakość</TableCell>
                  <TableCell>Bloker</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/*
                  GRUPOWANIE JEST TU TRE\u015aCI\u0104, NIE OZDOB\u0104.

                  Do 013 ta tabela by\u0142a jedn\u0105 p\u0142ask\u0105 list\u0105, w kt\u00f3rej wiersz "Szko\u0142y" (wymiar sprzeda\u017cy,
                  \u015bwie\u017co\u015b\u0107 NIEUSTALONA) s\u0105siadowa\u0142 z wierszem o katalogu da\u0144. Po zasileniu s\u0142ownik\u00f3w
                  z udowodnion\u0105 proweniencj\u0105 taki uk\u0142ad zach\u0119ca\u0142by do przeniesienia \u015bwie\u017co\u015bci jednego
                  na drugie \u2014 czyli do wniosku, \u017ce skoro s\u0142ownik szk\u00f3\u0142 jest bie\u017c\u0105cy, to dane o sprzeda\u017cy
                  w szko\u0142ach te\u017c. Nag\u0142\u00f3wek grupy stoi mi\u0119dzy nimi po to, \u017ceby to zdanie nie da\u0142o si\u0119
                  pomy\u015ble\u0107 przez przypadek.
                */}
                {GRUPY_KOLEJNOSC.filter((g) => zrodla.some((z) => z.grupa === g)).map((grupa) => (
                  <Fragment key={grupa}>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ background: 'var(--tlo-glebokie)', py: 1.25, borderBottom: 'none' }}>
                        {/* Poziom h5, nie h3: pozostałe sekcje tego ekranu są h5, a nagłówek wyżej
                            w hierarchii tworzył przeskok h3→h5 do następnej sekcji. Wyglądu to nie
                            zmienia — poziom nagłówka niesie ZNACZENIE, nie rozmiar. */}
                        <Typography
                          component="h4"
                          sx={{ fontSize: 12, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}
                        >
                          {OPIS_GRUPY[grupa].tytul}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
                          {OPIS_GRUPY[grupa].czym}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {zrodla.filter((z) => z.grupa === grupa).map((z) => (
                  <TableRow key={z.klucz} hover data-cc-card-id={z.kartaId}>
                    <TableCell sx={{ minWidth: 240 }}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Box
                          aria-hidden
                          sx={{ width: 8, height: 8, borderRadius: '50%', background: BARWA_STANU[z.stan], flex: 'none' }}
                        />
                        <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{z.nazwa}</Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
                        {z.czym}
                      </Typography>
                      <Typography variant="caption" sx={{ color: BARWA_STANU[z.stan], display: 'block', fontWeight: 700 }}>
                        {OPIS_STANU[z.stan]}
                      </Typography>
                      {z.podstawa ? (
                        <Box sx={{ mt: 0.5 }}>
                          <MetricBasisChips basis={z.podstawa} />
                        </Box>
                      ) : null}
                      <Box sx={{ ml: -1, mt: 0.5 }}>
                        <PrzyciskDowodu dowod={z.dowod} etykieta="Dowód" kartaId={z.dowodId} />
                      </Box>
                    </TableCell>

                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{z.srodowisko}</TableCell>

                    <TableCell sx={{ minWidth: 180 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={z.klasyfikacja === 'SWIEZOSC_NIEUSTALONA' ? 'warning' : 'default'}
                        label={z.klasyfikacja}
                      />
                      <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 0.5 }}>
                        {/* ZNACZNIK zamiast samego tekstu: kategoria pochodzenia ma byc
                            rozpoznawalna rzutem oka. Kolor NIE jest jedynym nosnikiem —
                            znacznik niesie pelna nazwe i opis w nazwie dostepnej. */}
                        <ZnacznikZrodla stan={stanZKlasyfikacji(z.klasyfikacja)} kompaktowy />
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ minWidth: 200, color: 'var(--tekst-2)' }}>{z.swiezosc}</TableCell>

                    <TableCell sx={{ minWidth: 176 }}>
                      {/* Brak daty NIE jest zerem ani pustym napisem — mówimy to wprost. */}
                      <Typography className="sygnal-liczba" sx={{ fontWeight: 700, fontSize: 13.5 }}>
                        {z.ostatniaData ?? (
                          <Box component="span" sx={{ fontStyle: 'italic', fontWeight: 400, color: 'var(--tekst-slaby)' }}>
                            brak danych
                          </Box>
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
                        {z.ostatniaData === null ? (powodBrakuDaty(z.stan) ?? z.oDacie) : z.oDacie}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ minWidth: 192 }}>
                      {/* MIERNIK mowi o SILE PROBY, znacznik wyzej — o pochodzeniu. Swiadomie
                          dwa urzadzenia, nie jedno: srednia ze swiezosci, wielkosci proby
                          i klasyfikacji nie odpowiadalaby na zadne z tych pytan. */}
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Miernik poziom={poziomZJakosci(z.jakosc.quality)} etykieta={z.nazwa} />
                        <DataQualityBadge quality={z.jakosc.quality} reason={z.jakosc.reason} />
                      </Stack>
                      <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 0.5 }}>
                        {z.jakosc.reason}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ minWidth: 240, color: 'var(--tekst-2)' }}>{z.bloker ?? '—'}</TableCell>
                  </TableRow>
                ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 1 }}>
            Daty sprzedaży i dostaw są ograniczone wybranym oknem {okno} — „ostatnia znana data" znaczy „ostatnia
            widoczna w tym oknie", a nie „ostatnia w historii". Katalog dań jest rejestrem i nie zależy od okna.
          </Typography>

          {/* Wykres pokazujemy WYŁĄCZNIE wtedy, gdy zbiór nie jest pusty. Seria dziennych zer
              narysowana dla pustego źródła wyglądałaby jak zmierzony wynik „codziennie zero". */}
          {seriaSprzedazy && !sprzedazPusta ? (
            <Box sx={{ mt: 2 }} data-cc-card-id="DATA_QUALITY-19">
              <Typography variant="body2" sx={{ color: 'var(--tekst-2)', mb: 1, maxWidth: '78ch' }}>
                Pokrycie okna przez dane sprzedaży. Słupek zerowy znaczy, że dzień JEST w serii i sprzedaż w nim
                wyniosła zero. Dzień, którego w serii nie ma, nie jest tu w ogóle widoczny — a tej różnicy nie da się
                zobaczyć po samej sumie.
              </Typography>
              <DailyTrendChart points={seriaSprzedazy} title="Pokrycie okna — dni z danymi sprzedaży" />
            </Box>
          ) : null}
        </Karta>

        <PasSekcji wariant="cichy" tytul="Braki pól" />

        {/*
          BRAKI PÓL — liczymy WYŁĄCZNIE to, co kontrakt pozwala uczciwie sklasyfikować.
          Pole OPCJONALNE, którego nie ma, NIE jest usterką jakości: kontrakt na nie pozwala.
          Usterką jest brak wartości tam, gdzie coś od niej zależy — i tylko takie przypadki
          ten blok nazywa. Nie budujemy tu licznika „ile pól brakuje", bo bez podziału na
          wymagane i opcjonalne byłaby to liczba bez znaczenia, która wygląda jak ocena.
        */}
        <Karta nadtytul="Nie każde puste pole jest usterką" tytul="Braki, które coś zmieniają">
          <Stack spacing={1}>
            <Wiersz
              etykieta="Dostawy bez identyfikatora szkoły"
              wartosc={raportZlaczenia ? formatCount(raportZlaczenia.delivery_rows_without_id) : 'nie policzono'}
              dopisek="Rekord dostawy, którego nie da się przypisać do szkoły, wypada ze złączenia — nie wchodzi do żadnej analizy łączonej i nie jest tam zerem."
            />
            <Wiersz
              etykieta="Źródła bez własnego znacznika czasu"
              wartosc={`${formatCount(zrodla.length - zUstalonaSwiezoscia)} z ${formatCount(zrodla.length)}`}
              dopisek="Odpowiedź nie niesie momentu odczytu. To brak pola WYMAGANEGO do orzeczenia o świeżości — dlatego liczymy go tutaj, a nie wśród pól opcjonalnych."
            />
            <Wiersz
              etykieta="Pola opcjonalne bez wartości"
              wartosc="nie liczone"
              dopisek="Kontrakt na nie pozwala, więc ich brak nie jest usterką jakości. Licznik mieszający je z brakami wymaganymi byłby liczbą, która wygląda jak ocena, a nią nie jest."
            />
          </Stack>
        </Karta>

        <PasSekcji wariant="cichy" tytul="Pokrycie złączeń" />

        {/*
          POKRYCIE ZŁĄCZENIA — z JAWNYM licznikiem i mianownikiem.

          Ta liczba była dotąd wyłącznie w szufladzie dowodu przy słowniku szkół, czyli widoczna
          po kliknięciu. Na ekranie o jakości danych pokrycie złączenia jest jedną z pięciu
          głównych odpowiedzi i musi stać na wierzchu.

          NIEDOPASOWANE NIE JEST ZEREM i nie jest brakiem bytu biznesowego: szkoła widoczna
          tylko po stronie sprzedaży istnieje — po prostu nie ma odpowiednika po stronie dostaw.
        */}
        <Karta nadtytul="Sprzedaż CRM ↔ dostawy Drive" tytul="Ile z dwóch list da się zestawić">
          {raportZlaczenia ? (
            <Stack spacing={1}>
              <Wiersz
                etykieta="Pokrycie (licznik ÷ mianownik)"
                wartosc={`${formatCount(raportZlaczenia.matched)} ÷ ${formatCount(raportZlaczenia.sales_schools)}`}
                dopisek="Licznik: szkoły obecne po OBU stronach. Mianownik: szkoły po stronie sprzedaży. Udział podaje płyta wyżej — tu stoją liczby, z których powstał."
              />
              <Wiersz
                etykieta="Tylko sprzedaż (bez odpowiednika w dostawach)"
                wartosc={formatCount(raportZlaczenia.sales_only)}
                dopisek="Te szkoły ISTNIEJĄ. Nie mają pary po drugiej stronie, więc wypadają ze złączenia — to nie jest ich zero."
              />
              <Wiersz
                etykieta="Tylko dostawy (bez odpowiednika w sprzedaży)"
                wartosc={formatCount(raportZlaczenia.delivery_only)}
                dopisek="Jak wyżej, z drugiej strony."
              />
              <Wiersz
                etykieta="Stan złączenia"
                wartosc={raportZlaczenia.status}
                dopisek={powodZlaczenia ?? raportZlaczenia.reason}
              />
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: 'var(--tekst-2)', maxWidth: '78ch' }}>
              Złączenia NIE POLICZONO — brak jednej ze stron albo brak uprawnienia do danych dostaw.
              To nie znaczy, że pokrycie wynosi zero: znaczy, że nie ma czego zestawiać, więc żadna
              liczba pokrycia nie istnieje. Wszystko, co ten ekran mówi o relacji sprzedaż ↔ dostawy,
              jest w tym stanie nieustalone.
            </Typography>
          )}
        </Karta>

        <PasSekcji wariant="cichy" tytul="Rodowód wydania" />

        <Box>
          {/* Tytuł bloku zniknął, bo dział nad nim mówi dokładnie to samo. Dwa nagłówki
              o identycznej treści to nie podkreślenie — to dwa nagłówki, z których jeden
              czytnik ekranu ogłasza bez powodu. */}
          <Typography variant="body2" sx={{ color: 'var(--tekst-2)', mb: 1.5, maxWidth: '78ch' }}>
            Publiczne E2E potwierdza KONKRETNY artefakt, opisany identyfikatorami poniżej. Dopóki identyfikatory
            wpieczone w artefakt zgadzają się z tym, czego klient realnie używa, akceptacja dotyczy tego, co widzisz.
          </Typography>

          {dowodHistoryczny ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>DOWÓD Z TESTU JEST HISTORYCZNY</AlertTitle>
              <Typography variant="body2">
                {nieprzypisywalny
                  ? 'Tego artefaktu nie da się jednoznacznie powiązać z commitem (brak SHA albo build z niezacommitowanego drzewa), więc żadna wcześniejsza akceptacja nie może być do niego przypisana.'
                  : 'Identyfikatory wpieczone w artefakt różnią się od tych, których używa działający klient. Akceptacja z testu dotyczy innego artefaktu niż ten, który właśnie działa.'}{' '}
                Stara akceptacja nie może wyglądać na bieżącą — do czasu ponownego uruchomienia testów przeciw temu
                wydaniu traktuj dowód jako historyczny.
              </Typography>
              {rozjazdy.length > 0 ? (
                <Stack component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }} spacing={0.5}>
                  {rozjazdy.map((r) => (
                    <Typography key={r.pole} component="li" variant="body2">
                      {r.pole}: w artefakcie <code>{r.wArtefakcie}</code>, w kliencie <code>{r.wKliencie}</code>
                    </Typography>
                  ))}
                </Stack>
              ) : null}
            </Alert>
          ) : null}

          <Paper variant="outlined" sx={{ p: 2.5 }} data-testid="build-info">
            <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)', mb: 1.5 }}>
              {describeBuild(buildInfo)}
            </Typography>
            <WierszRodowodu etykieta="Wersja aplikacji" wartosc={buildInfo.appVersion} />
            <WierszRodowodu etykieta="Commit" wartosc={shortSha(buildInfo.gitSha)} testid="build-info-sha" />
            <WierszRodowodu etykieta="Gałąź" wartosc={buildInfo.gitBranch} />
            <WierszRodowodu etykieta="Zbudowano" wartosc={buildInfo.buildTime} />
            <WierszRodowodu
              etykieta="Środowisko builda"
              wartosc={buildInfo.environment}
              rozjazd={rozjazdDla('Środowisko')}
            />
            <WierszRodowodu etykieta="Prefiks ścieżki" wartosc={buildInfo.base} rozjazd={rozjazdDla('Prefiks ścieżki')} />
            <WierszRodowodu
              etykieta="Adres Control Center w artefakcie"
              wartosc={buildInfo.api.cc}
              rozjazd={rozjazdDla('Adres Control Center')}
            />
            {buildInfo.gitDirty ? (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label="Build z niezacommitowanego drzewa — artefaktu nie da się jednoznacznie powiązać z commitem"
                sx={{ mt: 1.5, alignSelf: 'flex-start', height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
              />
            ) : null}
          </Paper>
        </Box>

        <PasSekcji tytul="Czy backendy w ogóle odpowiadają" />

        <Karta nadtytul="Jeden backend = jedna sonda" tytul="Sondy zdrowia backendów">
          <Typography variant="body2" sx={{ color: 'var(--tekst-2)', mb: 1.5, maxWidth: '78ch' }}>
            Pytanie pośrednie, ale rozstrzygające: gdy backend nie odpowiada, pustka na ekranie analitycznym nie jest
            wynikiem, tylko brakiem odczytu. Sondy są wyłącznie odczytowe.
          </Typography>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Sondy zdrowia backendów">
              <TableHead>
                <TableRow>
                  <TableCell>System</TableCell>
                  <TableCell>Adres sondy</TableCell>
                  <TableCell>Stan</TableCell>
                  <TableCell>Uwagi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {SONDY.map((sonda) => (
                  <WierszSondy key={sonda.system} sonda={sonda} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
            <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
              Warunki odczytu
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'var(--tekst-2)' }}>
                Sesje:{' '}
                {auth.providers
                  .map((p) => {
                    const id = auth.realms[p.realm]?.identity;
                    return `${p.label}: ${id ? `${id.subject} (${id.roles.join(', ')})` : 'nie podłączono'}`;
                  })
                  .join(' · ')}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'var(--krój-mono)', fontSize: 12, color: 'var(--tekst-2)' }}>
                Uprawnienia: {auth.permissions.size ? [...auth.permissions].join(', ') : '—'}
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 1 }}>
              „Niedostępne" znaczy wyłącznie, że pod tym adresem nic nie odpowiada w tym środowisku — nie znaczy awarii
              systemu produkcyjnego. Uprawnienia ograniczają to, co Control Center w ogóle ma prawo odczytać, więc
              węższe konto może zobaczyć mniej danych bez żadnej usterki po stronie źródła.
            </Typography>
          </Paper>
        </Karta>
      </Stack>
    </Box>
  );
}
